import "./MenuEventPreview.scss"

import { DesignViewer, MenuDesign } from "@streamllc/figma-design-viewer"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"

import { EventDto } from "@/entities/app/menu/menu.dto"
import DesignDAO from "@/entities/design/design.dao"
import { loadGoogleFont, loadPrivateFonts } from "@/entities/fonts/fonts.helpers"
import Button from "@/shared/components/intrinsic/Button/Button"

export interface MenuEventPreviewProps {
  event: EventDto
  companyId: string
  onEdit: () => void
}

const DAYS_MAP: Record<string, string> = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat"
}

function MenuEventPreview(props: MenuEventPreviewProps) {
  const { event, companyId, onEdit } = props

  // Fetch design if designId exists
  const { data: design, isLoading: isLoadingDesign } = useQuery({
    queryKey: ["design", event.designId],
    queryFn: () => {
      if (!event.designId) return null
      const designDao = new DesignDAO()
      return designDao.findById(event.designId)
    },
    enabled: !!event.designId,
    // Reset to null when designId is removed
    placeholderData: event.designId ? undefined : null
  })

  // Parse design JSON and create MenuDesign instance for DesignViewer
  const designData = useMemo(() => {
    if (!design) return null
    try {
      const node = JSON.parse(design.json)
      return new MenuDesign(node)
    } catch (error) {
      console.error("Failed to parse design JSON:", error)
      return null
    }
  }, [design])

  // Load fonts for the design
  useEffect(() => {
    if (designData) {
      designData.fonts.forEach(font => loadGoogleFont(font.family))
      void loadPrivateFonts(companyId)
    }
  }, [designData, companyId])

  // Resolver for image hashes
  function getMediaUrl(hash: string): string {
    const url = new URL(`designImage-${companyId}-${hash}`, import.meta.env.VITE_STORAGE_URL)
    return url.toString()
  }

  function formatTime(time: string): string {
    if (!time) return ""
    const [hours, minutes] = time.split(":")
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? "PM" : "AM"
    const hour12 = hour % 12 || 12
    return `${hour12}:${minutes} ${ampm}`
  }

  function formatDateTime(date: string, time: string): string {
    if (!date || !time) return ""
    const dateObj = new Date(date)
    const month = dateObj.toLocaleDateString("en-US", { month: "short" })
    const day = dateObj.getDate()
    const year = dateObj.getFullYear().toString().slice(-2)

    // Convert 24h time to 12h format
    const [hours, minutes] = time.split(":")
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? "PM" : "AM"
    const hour12 = hour % 12 || 12

    return `${month} ${day}, ${year}  ${hour12}:${minutes} ${ampm}`
  }

  function getTimingLabel(): string {
    if (event.recurrence === "daily") {
      return "Daily Schedule"
    }
    if (event.recurrence === "specific_days") {
      return "Weekly Schedule"
    }
    return "One-Time Event"
  }

  function getActivationMode(): string {
    if (event.recurrence === "daily") {
      return "Repeats Daily"
    }
    if (event.recurrence === "specific_days") {
      return "Repeats on Selected Days"
    }
    return "Specific Date Range"
  }

  function getActivePeriod(): string {
    if (event.recurrence === "daily") {
      // Daily mode: just show the time range
      return `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`
    }

    if (event.recurrence === "specific_days") {
      // Specific days mode: show time range
      return `${formatTime(event.start_time)} - ${formatTime(event.end_time)}`
    }

    // Specific dates mode: show full date and time range
    const startStr = formatDateTime(event.specific_start_date || "", event.start_time)
    const endStr = formatDateTime(event.specific_end_date || "", event.end_time)
    return `${startStr} - ${endStr}`
  }

  return (
    <div className="menu-event-preview">
      {/* Design Section */}
      <div className="menu-event-preview__section">
        <div className="menu-event-preview__header">
          <label className="menu-event-preview__label">Design Selected:</label>
          <Button color="white" size="smaller" onClick={onEdit}>
            Edit
          </Button>
        </div>

        {isLoadingDesign && event.designId && (
          <div className="menu-event-preview__loading">Loading design...</div>
        )}

        {!isLoadingDesign && event.designId && design && designData ? (
          <div className="menu-event-preview__design">
            <div className="menu-event-preview__design-preview">
              <DesignViewer
                design={designData}
                resolvers={{
                  imageHash: getMediaUrl,
                  transformText: content => content,
                  transformWidget() { }
                }}
                options={{ fit: "contain" }}
              />
            </div>
            <div className="menu-event-preview__design-name">{design.name}</div>
          </div>
        ) : !isLoadingDesign && (
          <div className="menu-event-preview__no-design">No design selected</div>
        )}
      </div>

      {/* Scheduler Overview Section */}
      <div className="menu-event-preview__section">
        <label className="menu-event-preview__label">Scheduler Overview:</label>
        <div className="menu-event-preview__overview-card">
          <div className="menu-event-preview__overview-row">
            <span className="menu-event-preview__overview-label">Timing:</span>
            <span className="menu-event-preview__overview-value">{getTimingLabel()}</span>
          </div>
          <div className="menu-event-preview__overview-row">
            <span className="menu-event-preview__overview-label">Activation Mode:</span>
            <span className="menu-event-preview__overview-value">{getActivationMode()}</span>
          </div>
          <div className="menu-event-preview__overview-row">
            <span className="menu-event-preview__overview-label">Active Period:</span>
            <span className="menu-event-preview__overview-value">{getActivePeriod()}</span>
          </div>
          {event.recurrence === "specific_days" && event.specific_days && event.specific_days.length > 0 && (
            <div className="menu-event-preview__overview-row">
              <span className="menu-event-preview__overview-label">Week Frequency:</span>
              <div className="menu-event-preview__days">
                {event.specific_days.map(day => (
                  <span key={day} className="menu-event-preview__day-badge">
                    {DAYS_MAP[day]}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="menu-event-preview__section">
        <label className="menu-event-preview__label">Notes:</label>
        <div className="menu-event-preview__notes-content">
          {event.description}
        </div>
      </div>
    </div>
  )
}

export default MenuEventPreview
